import { supabaseClient } from './supabase';
import { fetchGHGInventoryData } from '../components/charts/GHGInventorySupabase';
import { supabaseLocationService } from './supabaseLocationService';
import { PolicyScenario, fetchBaselineGHGData } from './policyImpactService';
import { 
  getCachedBaseline, 
  setCachedBaseline, 
  isBaselineCacheValid,
  BaselineProjections,
  clearBaselineCache,
  getOrCreateCalculationPromise
} from './policyBaselineCache';
import { getVMTDataForGHG } from './vmtDataService';
import { calculateTotalElectricityConsumption, calculateTotalNaturalGasConsumption, getBuildingsEnergyData } from './buildingsEnergyDataService';
import { fetchCarbonIntensityData } from './carbonIntensityService';

export interface TimeSeriesEmissionData {
  year: number;
  lightDutyEmissions: number;
  heavyDutyEmissions: number;
  naturalGasEmissions: number;
  electricityEmissions: number;
  wasteEmissions: number;
  offroadEmissions: number;
  totalEmissions: number;
}

export interface PolicyTimeSeriesResult {
  baselineData: TimeSeriesEmissionData[];
  projectedData: TimeSeriesEmissionData[];
  location: string;
}

function clampNumber(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min;
  return Math.min(max, Math.max(min, value));
}

type EmfacForecastRow = {
  year: number;
  forecasting_ratio?: number;
  ghg_per_mile?: number;
  total_vmt?: number;
};

function buildEmfacInterpolator(rows: EmfacForecastRow[]) {
  const byYear = new Map<number, EmfacForecastRow>();
  const years = rows
    .map((r) => Number(r.year))
    .filter((y) => Number.isFinite(y))
    .sort((a, b) => a - b);

  rows.forEach((r) => {
    const y = Number(r.year);
    if (!Number.isFinite(y)) return;
    byYear.set(y, { ...r, year: y });
  });

  const uniqYears = Array.from(new Set(years)).sort((a, b) => a - b);

  const findBounds = (year: number): { lo?: number; hi?: number } => {
    if (uniqYears.length === 0) return {};
    if (byYear.has(year)) return { lo: year, hi: year };
    let lo: number | undefined;
    let hi: number | undefined;
    for (let i = 0; i < uniqYears.length; i++) {
      const y = uniqYears[i];
      if (y < year) lo = y;
      if (y > year) {
        hi = y;
        break;
      }
    }
    return { lo, hi };
  };

  const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

  const interpolateNumber = (year: number, key: keyof EmfacForecastRow): number | undefined => {
    const exact = byYear.get(year);
    const exactVal = exact?.[key];
    if (typeof exactVal === 'number' && Number.isFinite(exactVal)) return exactVal;

    const { lo, hi } = findBounds(year);
    const loVal = lo !== undefined ? byYear.get(lo)?.[key] : undefined;
    const hiVal = hi !== undefined ? byYear.get(hi)?.[key] : undefined;

    // Interpolate if we have both sides
    if (
      lo !== undefined &&
      hi !== undefined &&
      lo !== hi &&
      typeof loVal === 'number' &&
      Number.isFinite(loVal) &&
      typeof hiVal === 'number' &&
      Number.isFinite(hiVal)
    ) {
      const t = (year - lo) / (hi - lo);
      return lerp(loVal, hiVal, t);
    }

    // Extrapolate using last two points (forward)
    if (lo !== undefined && hi === undefined && uniqYears.length >= 2) {
      const last = uniqYears[uniqYears.length - 1];
      const prev = uniqYears[uniqYears.length - 2];
      const lastVal = byYear.get(last)?.[key];
      const prevVal = byYear.get(prev)?.[key];
      if (
        typeof lastVal === 'number' &&
        Number.isFinite(lastVal) &&
        typeof prevVal === 'number' &&
        Number.isFinite(prevVal) &&
        last !== prev
      ) {
        const slopePerYear = (lastVal - prevVal) / (last - prev);
        return lastVal + slopePerYear * (year - last);
      }
    }

    // Extrapolate using first two points (backward)
    if (hi !== undefined && lo === undefined && uniqYears.length >= 2) {
      const first = uniqYears[0];
      const next = uniqYears[1];
      const firstVal = byYear.get(first)?.[key];
      const nextVal = byYear.get(next)?.[key];
      if (
        typeof firstVal === 'number' &&
        Number.isFinite(firstVal) &&
        typeof nextVal === 'number' &&
        Number.isFinite(nextVal) &&
        next !== first
      ) {
        const slopePerYear = (nextVal - firstVal) / (next - first);
        return firstVal - slopePerYear * (first - year);
      }
    }

    return undefined;
  };

  const getRow = (year: number): EmfacForecastRow | undefined => {
    const exact = byYear.get(year);
    if (exact) return exact;
    if (uniqYears.length === 0) return undefined;
    // Build a synthetic row with interpolated values when missing
    const forecasting_ratio = interpolateNumber(year, 'forecasting_ratio');
    const ghg_per_mile = interpolateNumber(year, 'ghg_per_mile');
    const total_vmt = interpolateNumber(year, 'total_vmt');
    if (
      typeof forecasting_ratio !== 'number' &&
      typeof ghg_per_mile !== 'number' &&
      typeof total_vmt !== 'number'
    ) {
      return undefined;
    }
    return { year, forecasting_ratio, ghg_per_mile, total_vmt };
  };

  return { getRow, years: uniqYears };
}

async function fetchCountyPopulationByYear(location: string): Promise<Record<number, number>> {
  try {
    await supabaseLocationService.initialize();
  } catch {
    // ignore; some callers already initialized
  }

  const resolvedCounty = (supabaseLocationService.getCountyForCity(location) || location).trim();
  const baseCountyName = resolvedCounty.replace(/\s+County$/i, '').trim();
  const variations = Array.from(
    new Set([resolvedCounty, baseCountyName, `${baseCountyName} County`].filter(Boolean))
  );

  for (const loc of variations) {
    const { data, error } = await supabaseClient
      .from('population')
      .select('year, population')
      .eq('location_name', loc)
      .order('year', { ascending: true });

    if (!error && data && data.length > 0) {
      const out: Record<number, number> = {};
      data.forEach((r: any) => {
        if (r?.year && r?.population) out[Number(r.year)] = Number(r.population);
      });
      return out;
    }
  }

  return {};
}

function fillAndExtrapolatePopulation(popByYear: Record<number, number>, startYear: number, endYear: number): Record<number, number> {
  const yearsAvail = Object.keys(popByYear).map((y) => parseInt(y, 10)).filter(Number.isFinite).sort((a, b) => a - b);
  if (yearsAvail.length === 0) return {};

  const lastYear = yearsAvail[yearsAvail.length - 1];
  const prevYear = yearsAvail[yearsAvail.length - 2] ?? lastYear - 1;
  const growthPerYear =
    lastYear > prevYear ? (popByYear[lastYear] - popByYear[prevYear]) / (lastYear - prevYear) : 0;

  const filled: Record<number, number> = { ...popByYear };

  // Forward extrapolation
  for (let y = lastYear + 1; y <= endYear; y++) {
    filled[y] = Math.max(1, Math.round(popByYear[lastYear] + growthPerYear * (y - lastYear)));
  }

  // Backfill earlier years (flat at earliest)
  const firstYear = yearsAvail[0];
  for (let y = startYear; y < firstYear; y++) {
    filled[y] = Math.max(1, Math.round(popByYear[firstYear]));
  }

  return filled;
}

/**
 * Fetch EMFAC county filtered data for VMT projection using forecasting_ratio
 * Uses the same approach as GHG Inventory to get ghg_per_mile values
 */
async function fetchEMFACForecastingData(location: string): Promise<any[]> {
  // Resolve county for the city to query EMFAC at the county level
  const resolvedCounty = (supabaseLocationService.getCountyForCity(location) || location).trim();
  const baseCountyName = resolvedCounty.replace(/\s+County$/i, '');
  const emfacLocation = `${baseCountyName} County`;
  
  // Try common naming variants
  const locationVariations = [
    emfacLocation,
    baseCountyName,
    `${baseCountyName} County`,
    resolvedCounty
  ];
  
  for (const loc of locationVariations) {
    console.log(`Policy Tool: Trying EMFAC county filtered for location: ${loc}`);
    const { data, error } = await supabaseClient
      .from('emfac_county_filtered')
      .select('year, forecasting_ratio, ghg_per_mile, total_vmt')
      .eq('location_name', loc)
      .eq('class', 'LDV')
      .gte('year', 2020)
      .lte('year', 2045)
      .order('year', { ascending: true });
    
    if (error) {
      console.log('Policy Tool: EMFAC county filtered query error for', loc, ':', error);
      continue;
    }
    
    if (data && data.length > 0) {
      console.log('Policy Tool: EMFAC county filtered data found for', loc, ':', data);
      return data;
    }
  }
  
  console.log('Policy Tool: No EMFAC county filtered data found for any location variation:', locationVariations);
  
  // Fallback: Try California data (same as GHG Inventory)
  const californiaVariations = ['California', 'CA', 'California State', 'State of California'];
  
  for (const californiaName of californiaVariations) {
    console.log(`Policy Tool: Trying California EMFAC county filtered: ${californiaName}`);
    const { data: californiaData, error: californiaError } = await supabaseClient
      .from('emfac_county_filtered')
      .select('year, forecasting_ratio, ghg_per_mile, total_vmt')
      .eq('location_name', californiaName)
      .eq('class', 'LDV')
      .gte('year', 2020)
      .lte('year', 2045)
      .order('year', { ascending: true });
    
    if (californiaError) {
      console.log(`Policy Tool: California EMFAC county filtered query error for ${californiaName}:`, californiaError);
      continue;
    }
    
    if (californiaData && californiaData.length > 0) {
      console.log(`Policy Tool: Using California EMFAC county filtered data for ${californiaName}:`, californiaData);
      return californiaData;
    }
  }
  
  console.log('Policy Tool: No EMFAC data available');
  return [];
}

/**
 * Calculate historical emissions (2005-2023) using the same method as GHG Inventory
 */
async function calculateHistoricalEmissions(location: string): Promise<TimeSeriesEmissionData[]> {
  try {
    // Get the GHG inventory data which already uses the correct calculation method
    const inventoryData = await fetchGHGInventoryData(location);
    
    // Extract year-specific historical data from the inventory result
    const historicalData: TimeSeriesEmissionData[] = [];
    
    // Get the chart data which contains year-specific emissions
    const chartData = inventoryData.chartData;
    const years = chartData.labels.map((year: any) => parseInt(year));
    
    console.log('Policy Tool: Available years in GHG inventory:', years);
    

    // Extract datasets for all categories
    const lightDutyDataset = chartData.datasets.find((d: any) => d.label === 'Light Duty Vehicles');
    const heavyDutyDataset = chartData.datasets.find((d: any) => d.label === 'Heavy Duty Vehicles');
    const naturalGasResDataset = chartData.datasets.find((d: any) => d.label === 'Natural Gas Residential');
    const naturalGasComDataset = chartData.datasets.find((d: any) => d.label === 'Natural Gas Commercial');
    const electricityResDataset = chartData.datasets.find((d: any) => d.label === 'Electricity Residential');
    const electricityComDataset = chartData.datasets.find((d: any) => d.label === 'Electricity Commercial');
    const wasteDataset = chartData.datasets.find((d: any) => d.label === 'Waste');
    const offroadDataset = chartData.datasets.find((d: any) => d.label === 'Offroad');

    // Process all years from 2005 to 2023
    for (let year = 2005; year <= 2023; year++) {
      const yearIndex = years.indexOf(year);
      
      if (yearIndex !== -1) {
        // Extract year-specific data from chart datasets
        // Use raw chart data for ALL years (2005-2023) to ensure continuity
        // The GHG inventory calculates 2023 the same way as other years (vmt * ghg_per_mile)
        // We should NOT recalculate 2023 here, as it would create a discontinuity with 2022
        const lightDutyEmissions = lightDutyDataset?.data[yearIndex] || 0;
        const heavyDutyEmissions = heavyDutyDataset?.data[yearIndex] || 0;
        
        // Combine residential and commercial natural gas emissions
        const naturalGasRes = naturalGasResDataset?.data[yearIndex] || 0;
        const naturalGasCom = naturalGasComDataset?.data[yearIndex] || 0;
        const naturalGasEmissions = naturalGasRes + naturalGasCom;
        
        // Combine residential and commercial electricity emissions
        const electricityRes = electricityResDataset?.data[yearIndex] || 0;
        const electricityCom = electricityComDataset?.data[yearIndex] || 0;
        const electricityEmissions = electricityRes + electricityCom;

        const wasteEmissions = wasteDataset?.data?.[yearIndex] || 0;
        const offroadEmissions = offroadDataset?.data?.[yearIndex] || 0;
        
        historicalData.push({
          year,
          lightDutyEmissions: Math.round(lightDutyEmissions),
          heavyDutyEmissions: Math.round(heavyDutyEmissions),
          naturalGasEmissions: Math.round(naturalGasEmissions),
          electricityEmissions: Math.round(electricityEmissions),
          wasteEmissions: Math.round(wasteEmissions),
          offroadEmissions: Math.round(offroadEmissions),
          totalEmissions: Math.round(lightDutyEmissions + heavyDutyEmissions + naturalGasEmissions + electricityEmissions + wasteEmissions + offroadEmissions)
        });
        
        if (year >= 2020 || year % 5 === 0) {
          console.log(`Policy Tool: Historical ${year}: LD=${lightDutyEmissions}, HD=${heavyDutyEmissions}, NG=${naturalGasEmissions}, EL=${electricityEmissions}`);
        }
      } else {
        // Fallback: use latest year's data if specific year not available
        const latestYearIndex = years.length - 1;
        const latestLightDuty = lightDutyDataset?.data[latestYearIndex] || 0;
        const latestHeavyDuty = heavyDutyDataset?.data[latestYearIndex] || 0;
        const latestNaturalGasRes = naturalGasResDataset?.data[latestYearIndex] || 0;
        const latestNaturalGasCom = naturalGasComDataset?.data[latestYearIndex] || 0;
        const latestElectricityRes = electricityResDataset?.data[latestYearIndex] || 0;
        const latestElectricityCom = electricityComDataset?.data[latestYearIndex] || 0;
        const latestWaste = wasteDataset?.data?.[latestYearIndex] || 0;
        const latestOffroad = offroadDataset?.data?.[latestYearIndex] || 0;
        
        historicalData.push({
          year,
          lightDutyEmissions: Math.round(latestLightDuty),
          heavyDutyEmissions: Math.round(latestHeavyDuty),
          naturalGasEmissions: Math.round(latestNaturalGasRes + latestNaturalGasCom),
          electricityEmissions: Math.round(latestElectricityRes + latestElectricityCom),
          wasteEmissions: Math.round(latestWaste),
          offroadEmissions: Math.round(latestOffroad),
          totalEmissions: Math.round(latestLightDuty + latestHeavyDuty + latestNaturalGasRes + latestNaturalGasCom + latestElectricityRes + latestElectricityCom + latestWaste + latestOffroad)
        });
      }
    }
    
    console.log('Policy Tool: Historical emissions calculated:', historicalData);
    return historicalData;
  } catch (error) {
    console.error('Error calculating historical emissions:', error);
    return [];
  }
}

/**
 * Project future emissions (2023-2045) using EMFAC forecasting data
 */
async function calculateProjectedEmissions(
  location: string, 
  historicalData: TimeSeriesEmissionData[],
  vmtReductionPercent: number = 0,
  evAdoptionPercent: number = 0,
  currentEVPercent: number = 0,
  scenario?: PolicyScenario
): Promise<TimeSeriesEmissionData[]> {
  try {
    // Get EMFAC forecasting data
    const emfacData = await fetchEMFACForecastingData(location);
    const emfacInterp = buildEmfacInterpolator((emfacData || []) as EmfacForecastRow[]);
    
    // Get VMT data from Transportation page (includes user customizations) - same as GHG Inventory
    let geofips: number | null = null;
    try {
      await supabaseLocationService.initialize();
      geofips = await supabaseLocationService.getGeofips(location);
    } catch (error) {
      console.warn('Could not get geofips for VMT data:', error);
    }

    // Buildings page inputs for projections:
    // - electricity consumption (kWh) by year
    // - natural gas consumption (therms) by year
    // - electricity carbon intensity (gCO2e/kWh) by year
    const buildingsEnergyData = await getBuildingsEnergyData(location);
    let electricityIntensityGPerKwhByYear: Record<number, number> = {};
    try {
      const geofipsStr = geofips ? String(geofips) : null;
      if (geofipsStr) {
        const ci = await fetchCarbonIntensityData(geofipsStr);
        const years = (ci.chartData?.labels || []) as any[];
        const data = (ci.chartData?.datasets?.[0]?.data || []) as any[];
        years.forEach((y: any, i: number) => {
          const yr = Number(y);
          const v = Number(data[i]);
          if (Number.isFinite(yr) && Number.isFinite(v) && v > 0) electricityIntensityGPerKwhByYear[yr] = v;
        });
      }
    } catch (e) {
      console.warn('Could not fetch carbon intensity series; electricity emissions will use fallback intensity.', e);
      electricityIntensityGPerKwhByYear = {};
    }
    const g2023 = electricityIntensityGPerKwhByYear[2023] || 300; // gCO2e/kWh fallback
    const cleanElectricityYear = 2045; // SB 100 default for baseline projections
    
    // Import and use the same VMT data service as GHG Inventory
    const { getVMTDataForGHG } = await import('./vmtDataService');
    const vmtData = await getVMTDataForGHG(location, geofips || undefined);
    console.log('VMT data for projection:', vmtData);
    
    // Get the latest VMT value (2023) as baseline
    const latestVmtData = vmtData.find(d => d.year === 2023);
    const baselineVmt = latestVmtData?.vmt || 0;
    
    console.log('Baseline VMT (2023):', baselineVmt);
    
    const projectedData: TimeSeriesEmissionData[] = [];
    
    // Calculate fallback average emission factor from available EMFAC data
    const validEmissionFactors = emfacData.filter(d => d.ghg_per_mile > 0);
    const avgEmissionFactor = validEmissionFactors.length > 0 
      ? validEmissionFactors.reduce((sum, d) => sum + d.ghg_per_mile, 0) / validEmissionFactors.length
      : 0.0008; // Fallback emission factor (tons CO2 per mile)
    
    console.log('Policy Tool: Fallback average emission factor:', avgEmissionFactor, 'tons CO2 per mile');
    console.log('Policy Tool: Available emission factors:', validEmissionFactors.map(d => `${d.year}: ${d.ghg_per_mile}`));
    
    // If we have an EMFAC emission factor for 2023, recompute LDV 2023 with it
    const emfac2023 = (emfacInterp.getRow(2023) as any) || emfacData.find(d => d.year === 2023 && d.ghg_per_mile > 0);
    
    // Pre-fetch baseline data and calculate grid intensity if EV adoption is used
    let baseline: any = null;
    let gridIntensity: number | null = null;
    const CONSTANTS = {
      BASELINE_GRID_INTENSITY: 0.2,
      ANNUAL_SOLAR_HOURS: 2000,
      SOLAR_DISPLACEMENT_FACTOR: 0.85,
      EV_KWH_PER_MILE: 0.3,
      ICE_IMPROVEMENT_FACTOR: 0.95,
    };
    
    // Calculate grid intensity if EV adoption is at or above current level
    // Always calculate if currentEVPercent > 0 to ensure baseline accounts for existing EV adoption
    // This ensures baseline uses fleet-weighted emission factor when there are existing EVs
    if (evAdoptionPercent >= currentEVPercent || currentEVPercent > 0) {
      try {
        baseline = await fetchBaselineGHGData(location);
        const solarMult = scenario?.solarCapacityMultiplier || 1;
        const solarCapacityKw = baseline.solarCapacityKw * solarMult;
        const totalElectricityConsumption = baseline.population * 7000;
        const solarGeneration = solarCapacityKw * CONSTANTS.ANNUAL_SOLAR_HOURS;
        const solarPenetration = totalElectricityConsumption > 0 ? solarGeneration / totalElectricityConsumption : 0;
        gridIntensity = CONSTANTS.BASELINE_GRID_INTENSITY * (1 - Math.min(solarPenetration * CONSTANTS.SOLAR_DISPLACEMENT_FACTOR, 0.9));
      } catch (e) {
        console.warn('Could not fetch baseline data for EV calculation:', e);
      }
    }

    // Population scaling (county) for projecting Waste / Natural Gas / Electricity
    const countyPopRaw = await fetchCountyPopulationByYear(location);
    const countyPopByYear = fillAndExtrapolatePopulation(countyPopRaw, 2005, 2045);
    const basePop = countyPopByYear[2023] || 1;

    for (let year = 2023; year <= 2045; year++) {
      // For 2023, use the historical data directly (which now uses fleet-weighted emission factor)
      // This ensures perfect continuity between historical and projected data
      if (year === 2023) {
        const historical2023 = historicalData.find(d => d.year === 2023);
        if (historical2023) {
          // Use historical 2023 value directly - it already has the fleet-weighted emission factor applied
          projectedData.push({
            year: 2023,
            lightDutyEmissions: historical2023.lightDutyEmissions,
            heavyDutyEmissions: historical2023.heavyDutyEmissions,
            naturalGasEmissions: historical2023.naturalGasEmissions,
            electricityEmissions: historical2023.electricityEmissions,
            wasteEmissions: historical2023.wasteEmissions,
            offroadEmissions: historical2023.offroadEmissions,
            totalEmissions: historical2023.totalEmissions
          });
          console.log(`🔍 Baseline 2023: Using historical value directly (LDV=${historical2023.lightDutyEmissions}, Total=${historical2023.totalEmissions})`);
        } else {
          // Fallback: calculate 2023 if historical data is missing
          const yearEmissionFactor2023 = emfac2023?.ghg_per_mile || avgEmissionFactor;
          let fleetEmissionFactor2023 = yearEmissionFactor2023;
          if (currentEVPercent > 0 && gridIntensity !== null) {
            const iceGhgPerMile = yearEmissionFactor2023 * CONSTANTS.ICE_IMPROVEMENT_FACTOR;
            const evGhgPerMileTons = (gridIntensity * CONSTANTS.EV_KWH_PER_MILE) / 1000;
            const icePercent = 100 - currentEVPercent;
            fleetEmissionFactor2023 = (icePercent / 100 * iceGhgPerMile) + (currentEVPercent / 100 * evGhgPerMileTons);
          }
          const lightDuty2023 = Math.round(baselineVmt * fleetEmissionFactor2023);
        projectedData.push({
          year: 2023,
          lightDutyEmissions: lightDuty2023,
            heavyDutyEmissions: 0,
            naturalGasEmissions: 0,
            electricityEmissions: 0,
            wasteEmissions: 0,
            offroadEmissions: 0,
            totalEmissions: lightDuty2023
          });
        }
        continue;
      }
      
      // Find EMFAC data for this year
      const emfacYearData = emfacInterp.getRow(year) || emfacData.find(d => d.year === year);
      
      // Get 2023 historical data for continuity
      const historical2023 = historicalData.find(d => d.year === 2023);
      const ldv2023 = historical2023?.lightDutyEmissions || 0;
      
      if (emfacYearData && emfacYearData.forecasting_ratio) {
        // Project VMT using forecasting ratio
        const projectedVmt = baselineVmt * emfacYearData.forecasting_ratio;
        
        // Use year-specific emission factor from EMFAC data, or fallback to average
        const yearEmissionFactor = emfacYearData.ghg_per_mile || avgEmissionFactor;
        
        // Calculate gradual VMT reduction percentage for this year
        // Gradual ramp: increases by targetVMTReduction% / (2045 - 2023) each year from 2024 to 2045
        // Formula: annualVMTReductionIncrease = targetVMTReduction% / (2045 - 2023)
        // For year Y: vmtReductionPercentThisYear = 0 + annualIncrease * (Y - 2023)
        const yearsTo2045 = 2045 - 2023; // Total years from 2023 to 2045 (22 years)
        const yearsElapsed = year - 2023; // Years elapsed since 2023
        const annualVMTReductionIncrease = vmtReductionPercent / yearsTo2045; // Annual increase in VMT reduction percentage
        
        // Calculate VMT reduction percentage for this year
        const vmtReductionPercentThisYear = yearsElapsed <= 0 
          ? 0 // 2023: no VMT reduction yet
          : Math.min(vmtReductionPercent, annualVMTReductionIncrease * yearsElapsed); // 2024-2045: gradual increase
        
        // Apply gradual VMT reduction from policy tool
        const reducedVmt = projectedVmt * (1 - vmtReductionPercentThisYear / 100);
        
        // Calculate EV adoption percentage for this year
        // Gradual ramp between current and target from 2023->2045, supporting both increases and decreases.
        const annualEVIncrease = (evAdoptionPercent - currentEVPercent) / yearsTo2045; // % points per year
        const evUnclamped =
          yearsElapsed <= 0 ? currentEVPercent : currentEVPercent + (annualEVIncrease * yearsElapsed);
        const evLowerBound = Math.min(currentEVPercent, evAdoptionPercent, 100);
        const evUpperBound = Math.max(currentEVPercent, evAdoptionPercent, 0);
        const evPercentThisYear = clampNumber(evUnclamped, Math.max(0, evLowerBound), Math.min(100, evUpperBound));
        
        // Debug for baseline calculation (2023-2026) when evAdoptionPercent equals currentEVPercent
        if (year <= 2026 && evAdoptionPercent === currentEVPercent) {
          console.log(`🔍 Baseline Calc ${year}: evAdoptionPercent=${evAdoptionPercent}, currentEVPercent=${currentEVPercent}, annualEVIncrease=${annualEVIncrease.toFixed(6)}, evPercentThisYear=${evPercentThisYear.toFixed(2)}, yearsElapsed=${yearsElapsed}`);
        }
        
        // Calculate fleet-weighted emission factor accounting for EV adoption
        // For baseline (evAdoptionPercent === currentEVPercent), use raw EMFAC factor to maintain continuity with 2023
        // For policy scenarios (evAdoptionPercent > currentEVPercent), use fleet-weighted factor to show additional EV adoption
        let fleetEmissionFactor = yearEmissionFactor;
        
        // Only apply fleet-weighted factor if EV adoption differs from baseline (either higher OR lower).
        // Baseline (no change) uses raw EMFAC factor for continuity with inventory.
        const additionalEVPercent = evAdoptionPercent - currentEVPercent;
        if (additionalEVPercent !== 0 && gridIntensity !== null) {
          // Calculate ICE and EV emission factors
          const iceGhgPerMile = yearEmissionFactor * CONSTANTS.ICE_IMPROVEMENT_FACTOR;
          const evGhgPerMile = gridIntensity * CONSTANTS.EV_KWH_PER_MILE; // kg CO2/kWh * kWh/mile = kg CO2/mile, convert to tons
          const evGhgPerMileTons = evGhgPerMile / 1000; // Convert kg to tons
          
          const icePercent = 100 - evPercentThisYear;
          
          // Fleet-weighted emission factor: weighted average of ICE and EV emission factors
          fleetEmissionFactor = (icePercent / 100 * iceGhgPerMile) + (evPercentThisYear / 100 * evGhgPerMileTons);
        }
        // If additionalEVPercent === 0 (baseline), use raw EMFAC factor (same as 2023) for continuity
        
        // Calculate emissions: VMT * emission factor
        const lightDutyEmissions = reducedVmt * fleetEmissionFactor;
        const effectiveEmissionFactor = fleetEmissionFactor;
        
        // Log EV adoption and VMT reduction progress for debugging (log first few years and every 5 years)
        if (year <= 2027 || year % 5 === 0 || year === 2045) {
          console.log(`Year ${year}: VMT Reduction=${vmtReductionPercentThisYear.toFixed(2)}% (target: ${vmtReductionPercent}%, annual increase: ${annualVMTReductionIncrease.toFixed(3)}%/year), EV%=${evPercentThisYear.toFixed(2)}% (target: ${evAdoptionPercent}%, annual increase: ${annualEVIncrease.toFixed(3)}%/year), Fleet EF=${effectiveEmissionFactor.toFixed(6)} tons/mile, Emissions=${lightDutyEmissions.toFixed(0)} tons CO2e`);
        }
        
        // For other sources:
        // - Waste scales by county population.
        // - Natural Gas and Electricity use Buildings-page consumption (therms/kWh) + (g/kWh) intensity.
        // - Heavy Duty + Offroad: scale by population and decarbonize 1.5%/year.
        const hdvOffroadDecarbFactor = Math.pow(0.985, year - 2023);

        const pop = countyPopByYear[year] || basePop;
        const popFactor = basePop > 0 ? pop / basePop : 1;

        // Buildings: compute electricity + natural gas emissions from consumption and intensity.
        // Natural gas: 5.29 kg CO2e/therm => 0.00529 metric tons/therm
        const totalTherms = calculateTotalNaturalGasConsumption(buildingsEnergyData, year);
        const naturalGasEmissions = (totalTherms * 5.29) / 1000;

        const totalKwh = calculateTotalElectricityConsumption(buildingsEnergyData, year);
        const denom = Math.max(1, cleanElectricityYear - 2023);
        const t = clampNumber((year - 2023) / denom, 0, 1);
        const gPerKwh = g2023 * (1 - t);
        const electricityEmissions = (totalKwh * gPerKwh) / 1_000_000; // g -> metric tons
        
        projectedData.push({
          year,
          lightDutyEmissions: Math.round(lightDutyEmissions),
          heavyDutyEmissions: Math.round((historical2023?.heavyDutyEmissions || 0) * popFactor * hdvOffroadDecarbFactor),
          naturalGasEmissions: Math.round(naturalGasEmissions),
          electricityEmissions: Math.round(electricityEmissions),
          wasteEmissions: Math.round((historical2023?.wasteEmissions || 0) * popFactor),
          offroadEmissions: Math.round((historical2023?.offroadEmissions || 0) * popFactor * hdvOffroadDecarbFactor),
          totalEmissions: Math.round(lightDutyEmissions) + 
                         Math.round((historical2023?.heavyDutyEmissions || 0) * popFactor * hdvOffroadDecarbFactor) +
                         Math.round(naturalGasEmissions) +
                         Math.round(electricityEmissions) +
                         Math.round((historical2023?.wasteEmissions || 0) * popFactor) +
                         Math.round((historical2023?.offroadEmissions || 0) * popFactor * hdvOffroadDecarbFactor)
        });
      } else {
        // Fallback: use historical data with growth
        const historical2023 = historicalData.find(d => d.year === 2023);
        const growthFactor = 1 + (year - 2023) * 0.02;

        const pop = countyPopByYear[year] || basePop;
        const popFactor = basePop > 0 ? pop / basePop : 1;
        const hdvOffroadDecarbFactor = Math.pow(0.985, year - 2023);

        const totalTherms = calculateTotalNaturalGasConsumption(buildingsEnergyData, year);
        const naturalGasEmissions = (totalTherms * 5.29) / 1000;

        const totalKwh = calculateTotalElectricityConsumption(buildingsEnergyData, year);
        const denom = Math.max(1, cleanElectricityYear - 2023);
        const t = clampNumber((year - 2023) / denom, 0, 1);
        const gPerKwh = g2023 * (1 - t);
        const electricityEmissions = (totalKwh * gPerKwh) / 1_000_000;
        
        projectedData.push({
          year,
          lightDutyEmissions: Math.round((historical2023?.lightDutyEmissions || 0) * growthFactor),
          heavyDutyEmissions: Math.round((historical2023?.heavyDutyEmissions || 0) * popFactor * hdvOffroadDecarbFactor),
          naturalGasEmissions: Math.round(naturalGasEmissions),
          electricityEmissions: Math.round(electricityEmissions),
          wasteEmissions: Math.round((historical2023?.wasteEmissions || 0) * popFactor),
          offroadEmissions: Math.round((historical2023?.offroadEmissions || 0) * popFactor * hdvOffroadDecarbFactor),
          totalEmissions: Math.round((historical2023?.lightDutyEmissions || 0) * growthFactor) +
                         Math.round((historical2023?.heavyDutyEmissions || 0) * popFactor * hdvOffroadDecarbFactor) +
                         Math.round(naturalGasEmissions) +
                         Math.round(electricityEmissions) +
                         Math.round((historical2023?.wasteEmissions || 0) * popFactor) +
                         Math.round((historical2023?.offroadEmissions || 0) * popFactor * hdvOffroadDecarbFactor)
        });
      }
    }
    
    console.log('Projected emissions calculated:', projectedData);
    return projectedData;
  } catch (error) {
    console.error('Error calculating projected emissions:', error);
    return [];
  }
}

/**
 * Calculate and cache baseline projections (expensive, done once per location)
 * This includes all the expensive database queries and calculations
 */
async function calculateAndCacheBaseline(location: string): Promise<BaselineProjections> {
  console.log('📊 Calculating baseline projections for', location, '(this may take a few seconds)...');
  
  // Calculate historical emissions (2005-2023)
    const historicalData = await calculateHistoricalEmissions(location);
    
    // Fetch baseline data to get current EV percentage
    const baseline = await fetchBaselineGHGData(location);
    const currentEVPercent = baseline.currentEVPercent || 0;
  
  // Get VMT data (includes user customizations)
  let geofips: number | null = null;
  try {
    await supabaseLocationService.initialize();
    geofips = await supabaseLocationService.getGeofips(location);
  } catch (error) {
    console.warn('Could not get geofips for VMT data:', error);
  }
  const vmtData = await getVMTDataForGHG(location, geofips || undefined);
  const latestVmtData = vmtData.find(d => d.year === 2023);
  const baselineVmt = latestVmtData?.vmt || 0;
  
  // Get EMFAC forecasting data
  const emfacData = await fetchEMFACForecastingData(location);
  
  // Get population data
  const countyPopRaw = await fetchCountyPopulationByYear(location);
  const countyPopByYear = fillAndExtrapolatePopulation(countyPopRaw, 2005, 2045);
  
  // Get buildings energy data (includes user customizations)
  const buildingsEnergyData = await getBuildingsEnergyData(location);

  // Get electricity carbon intensity series (gCO2e/kWh) for the location (Buildings page input)
  let electricityCarbonIntensityGPerKwhByYear: Record<number, number> = {};
  try {
    let geofipsForCI: number | null = null;
    try {
      await supabaseLocationService.initialize();
      geofipsForCI = await supabaseLocationService.getGeofips(location);
    } catch {
      geofipsForCI = null;
    }
    if (geofipsForCI) {
      const ci = await fetchCarbonIntensityData(String(geofipsForCI));
      const years = (ci.chartData?.labels || []) as any[];
      const data = (ci.chartData?.datasets?.[0]?.data || []) as any[];
      years.forEach((y: any, i: number) => {
        const yr = Number(y);
        const v = Number(data[i]);
        if (Number.isFinite(yr) && Number.isFinite(v) && v > 0) electricityCarbonIntensityGPerKwhByYear[yr] = v;
      });
    }
  } catch (e) {
    console.warn('Policy Tool: could not fetch carbon intensity series for baseline cache', e);
    electricityCarbonIntensityGPerKwhByYear = {};
  }
  
  // Calculate baseline projections (2024-2045) with NO policies
  // Note: 2023 is the baseline year and comes from historical data, not projections
  // The baseline should have evAdoptionPercent = currentEVPercent (no change)
  console.log('🔍 calculateAndCacheBaseline: Calculating baseline with currentEVPercent=', currentEVPercent);
  const baselineProjected = await calculateProjectedEmissions(
      location, 
      historicalData, 
    0, // No VMT reduction
    currentEVPercent, // No EV adoption change (target = current)
      currentEVPercent, // Current EV percent (baseline)
    undefined // No scenario (baseline only)
  );
  console.log('🔍 calculateAndCacheBaseline: Baseline projected years:', baselineProjected.map(d => `${d.year}: LDV=${d.lightDutyEmissions}`).slice(0, 5));
  
  // Create hash of VMT data for cache invalidation (using hash functions from cache service)
  const { hashVMTData, hashBuildingsEnergyData } = await import('./policyBaselineCache');
  const vmtHash = hashVMTData(vmtData);
  const energyHash = hashBuildingsEnergyData(buildingsEnergyData);

  // Get 2023 from historical data (which now uses fleet-weighted emission factor for consistency)
  // This ensures perfect continuity between historical and projected data
  const historical2023 = historicalData.find(d => d.year === 2023);
  
  // Baseline projections: 2023 (from historical, with fleet-weighted EF) + 2024-2045 (from projections)
  // 2023 is the baseline year, calculated with fleet-weighted emission factor if currentEVPercent > 0
  // This ensures smooth continuity with 2024+ calculations
  const baselineProjectionsArray = historical2023
    ? [historical2023, ...baselineProjected.filter(d => d.year >= 2024)]
    : baselineProjected.filter(d => d.year >= 2024);
  
  const baselineProjections: BaselineProjections = {
    location,
    vmtHash,
    energyHash,
    historicalData,
    baselineProjections: baselineProjectionsArray, // 2023 (historical) + 2024-2045 (projections)
    baselineVMT: baselineVmt,
      currentEVPercent,
    solarCapacityKw: baseline.solarCapacityKw || 0,
    population: baseline.population || 0,
    emfacData,
    populationData: countyPopByYear,
    buildingsEnergyData,
    electricityCarbonIntensityGPerKwhByYear,
    calculationDate: new Date()
  };
  
  // Cache the baseline
  setCachedBaseline(location, baselineProjections);
  
  console.log('✅ Baseline projections calculated and cached for', location);
  return baselineProjections;
}

/**
 * Apply policy transformations to cached baseline (instant, lightweight)
 * This performs only math operations - no database queries
 */
export function applyPolicyTransformations(
  baseline: BaselineProjections,
  scenario: PolicyScenario
): TimeSeriesEmissionData[] {
  const targetEVPercent = scenario.evAdoptionPercent || 0;
  const vmtReductionPercent = scenario.vmtReductionPercent || 0;
  const yearsTo2045 = 2045 - 2023;
  
  // Policy multipliers (instant calculations)
    const hdvScale = Math.max(0, 1 - (scenario.hdvElectrificationPercent || 0) / 100);
    const solarMult = scenario.solarCapacityMultiplier || 1;
    const targetEnergyEfficiencyPercent = Math.max(0, Math.min(40, scenario.energyEfficiencyPercent || 0));
    const targetBuildingElectrificationPercent = Math.max(0, Math.min(100, scenario.heatPumpAdoptionPercent || 0));
    const targetOrganicsDiversionPercent = Math.max(50, Math.min(100, (scenario as any).organicsDiversionPercent || 50));
    const cleanElectricityYear = Math.max(2025, Math.min(2045, (scenario as any).gridCleanElectricityYear ?? 2045));

  // Calculate grid intensity for EV adoption (if needed)
  const CONSTANTS = {
    BASELINE_GRID_INTENSITY: 0.2,
    ANNUAL_SOLAR_HOURS: 2000,
    SOLAR_DISPLACEMENT_FACTOR: 0.85,
    EV_KWH_PER_MILE: 0.3,
    ICE_IMPROVEMENT_FACTOR: 0.95,
  };
  
  // Get EMFAC data for emission factors
  const validEmissionFactors = baseline.emfacData.filter(d => d.ghg_per_mile > 0);
  const avgEmissionFactor = validEmissionFactors.length > 0 
    ? validEmissionFactors.reduce((sum, d) => sum + d.ghg_per_mile, 0) / validEmissionFactors.length
    : 0.0008;
  const emfacInterp = buildEmfacInterpolator((baseline.emfacData || []) as EmfacForecastRow[]);
  const ciByYear = baseline.electricityCarbonIntensityGPerKwhByYear || {};
  const ciFallback2023 = ciByYear[2023] || 300; // gCO2e/kWh fallback
  const baseG2023 = ciByYear[2023] || ciFallback2023;
  
  // Calculate grid intensity for EV charging using Clean Electricity Year logic
  // (linearly reduce from 2023 Buildings-page carbon intensity to 0 by cleanElectricityYear)
  const getGridIntensityForYear = (year: number): number => {
    const denom = Math.max(1, cleanElectricityYear - 2023);
    const t = clampNumber((year - 2023) / denom, 0, 1);
    return baseG2023 * (1 - t); // g CO2e/kWh
  };
  
  const computeFleetEmissionFactorFromYearBase = (
    evPercentPolicy: number,
    yearFleetEmissionFactor: number,
    baselineEvPercent: number,
    year: number
  ): number => {
    // `yearFleetEmissionFactor` comes from EMFAC `ghg_per_mile` for that year.
    // EMFAC appears to be fleet-average, so we first infer an ICE-only factor using baseline EV share,
    // then recompute a fleet-average using the policy EV share for that year.
    const baseFleet = Number.isFinite(yearFleetEmissionFactor) ? yearFleetEmissionFactor : 0;
    
    // Calculate year-specific grid intensity using Clean Electricity Year logic
    // Convert g/kWh to kg/kWh, then to tons/mile
    const gridIntensityGPerKwh = getGridIntensityForYear(year);
    const gridIntensityKgPerKwh = gridIntensityGPerKwh / 1000;
    const evFactor = (gridIntensityKgPerKwh * CONSTANTS.EV_KWH_PER_MILE) / 1000; // tons/mi
    const baseEvFrac = clampNumber(baselineEvPercent || 0, 0, 99.999) / 100;
    const policyEvFrac = clampNumber(evPercentPolicy || 0, 0, 100) / 100;

    // If baseline EV share is 0, we can't decompose; treat EMFAC as ICE-only for lack of better info.
    const iceOnly =
      baseEvFrac > 0 && baseEvFrac < 1
        ? Math.max(0, (baseFleet - baseEvFrac * evFactor) / Math.max(1e-6, 1 - baseEvFrac))
        : baseFleet;

    return (1 - policyEvFrac) * iceOnly + policyEvFrac * evFactor;
  };
  
  const basePop = baseline.populationData[2023] || 1;
  const historical2023 = baseline.historicalData.find(d => d.year === 2023);
  
  // Apply transformations to each projected year
  // IMPORTANT: 2023 is the baseline year - no policies should be applied to it
  console.log('🔍 applyPolicyTransformations: Starting transformations');
  console.log('🔍 Baseline currentEVPercent:', baseline.currentEVPercent);
  console.log('🔍 Target EV percent:', targetEVPercent);
  console.log('🔍 Baseline projections years:', baseline.baselineProjections.map(d => d.year));
  
  const transformedProjections = baseline.baselineProjections.map((baseRow) => {
    const year = baseRow.year;
    const yearsElapsed = year - 2023;
    
    // EV adoption ramping
    // Only apply the ADDITIONAL EV adoption above current baseline
    // If target equals current (slider at default), no change should occur
    const additionalEVPercent = targetEVPercent - baseline.currentEVPercent;
    
    // Debug for 2023-2026 to understand what's happening
    if (year <= 2026) {
      console.log(`🔍 Year ${year}: yearsElapsed=${yearsElapsed}, additionalEVPercent=${additionalEVPercent.toFixed(2)}%, baseRow.lightDutyEmissions=${baseRow.lightDutyEmissions}`);
    }
    
    // Calculate EV% for this year by linearly interpolating between current and target
    // (supports both increases and decreases; prevents step-changes in 2024).
    const totalSteps = 2045 - 2023; // 22 steps
    const annualEVIncrease = additionalEVPercent / totalSteps;
    const evUnclamped =
      yearsElapsed <= 0 ? baseline.currentEVPercent : baseline.currentEVPercent + (annualEVIncrease * yearsElapsed);
    const evLowerBound = Math.min(baseline.currentEVPercent, targetEVPercent);
    const evUpperBound = Math.max(baseline.currentEVPercent, targetEVPercent);
    const evPercentThisYear = clampNumber(evUnclamped, Math.max(0, evLowerBound), Math.min(100, evUpperBound));
    const additionalEVPercentThisYear = evPercentThisYear - baseline.currentEVPercent;
    
    // Debug log for baseline year (2023) and first few years to verify smooth ramping
    if (year <= 2026) {
      if (additionalEVPercent > 0) {
        console.log(`Policy Transform ${year}: additionalEVPercent=${additionalEVPercent.toFixed(2)}%, annualIncrease=${annualEVIncrease.toFixed(3)}%, additionalThisYear=${additionalEVPercentThisYear.toFixed(3)}%, evPercentThisYear=${evPercentThisYear.toFixed(2)}%`);
      } else {
        console.log(`Policy Transform ${year}: No additional EV adoption (baseline year or default slider)`);
      }
    }
    
    // VMT reduction ramping (same logic as EV adoption)
    const annualVMTReductionIncrease = vmtReductionPercent / totalSteps;
    const vmtReductionPercentThisYear = Math.min(vmtReductionPercent, annualVMTReductionIncrease * yearsElapsed);
    
    // 2023 is the baseline year - return it unchanged (no policies applied)
    // This check happens after calculating yearsElapsed but before applying any changes
    if (year === 2023) {
      console.log(`✅ Year ${year}: Returning baseline row unchanged (baseline year)`);
      return baseRow;
    }
    
    // If no additional EV adoption and no other policy changes, return baseline row unchanged
    // This ensures slider at default behaves exactly like zero - no drop in 2024
    // Check both the total policy and the year-specific reduction to catch edge cases
    if (additionalEVPercent === 0 && vmtReductionPercent === 0 && 
        (scenario.hdvElectrificationPercent || 0) === 0 &&
        targetBuildingElectrificationPercent === 0 &&
        targetEnergyEfficiencyPercent === 0 &&
        targetOrganicsDiversionPercent === 50 &&
        (scenario.solarCapacityMultiplier || 1) === 1 &&
        // Grid lever: only "no-op" when left at default 2045
        cleanElectricityYear === 2045) {
      if (year <= 2026) {
        console.log(`✅ Year ${year}: Returning baseline row unchanged (no policy changes)`);
      }
      return baseRow;
    }
    
    // Debug: If we're applying changes, log what's happening
    if (year <= 2026) {
      console.log(`⚠️ Year ${year}: Applying policy changes (additionalEVPercent=${additionalEVPercent.toFixed(2)}%, vmtReductionPercent=${vmtReductionPercent.toFixed(2)}%)`);
    }
    
    // Find EMFAC data for this year
    const emfacYearData = emfacInterp.getRow(year) || baseline.emfacData.find(d => d.year === year);
    const yearEmissionFactor = emfacYearData?.ghg_per_mile || avgEmissionFactor;
    // Use the year-specific EMFAC factor as the base so the LDV trajectory retains expected curvature
    // (from EMFAC/vmt forecasting). We'll adjust only the EV-share portion.
    const emissionFactorForEvPolicy = yearEmissionFactor;
    
    // Calculate projected VMT (from baseline) for this year
    const baselineVmt = baseline.baselineVMT * (emfacYearData?.forecasting_ratio || 1);
    const reducedVmt = baselineVmt * (1 - vmtReductionPercentThisYear / 100);
    
    // Calculate LDV emissions with policies
    // Start from baseline LDV emissions (which already accounts for current EV adoption)
    // Only adjust for additional EV adoption and VMT reduction
    let lightDutyEmissions: number;
    
    if (additionalEVPercent === 0 && vmtReductionPercentThisYear === 0) {
      // No additional EV adoption and no VMT reduction - use baseline LDV emissions exactly
      // This ensures no drop when slider is at default
      lightDutyEmissions = baseRow.lightDutyEmissions || 0;
    } else if (additionalEVPercent === 0) {
      // No additional EV adoption but VMT reduction - use baseline LDV emissions, adjusted only for VMT reduction
      // Baseline already has current EV adoption accounted for
      const baselineVmtRatio = baselineVmt > 0 ? reducedVmt / baselineVmt : 1;
      lightDutyEmissions = (baseRow.lightDutyEmissions || 0) * baselineVmtRatio;
    } else {
      // EV adoption differs from baseline (higher OR lower).
      // Compute LDV directly as VMT(year) * fleet EF(ev%),
      // preserving EMFAC's year-to-year trend while swapping the EV-share component.
      const fleetEmissionFactor = computeFleetEmissionFactorFromYearBase(
        evPercentThisYear,
        emissionFactorForEvPolicy,
        baseline.currentEVPercent || 0,
        year
      );
      lightDutyEmissions = reducedVmt * fleetEmissionFactor;
    }
    
    // Apply population scaling and decarbonization factors
    const pop = baseline.populationData[year] || basePop;
    const popFactor = basePop > 0 ? pop / basePop : 1;
    const decarbFactor = Math.pow(0.95, year - 2023);
    const hdvOffroadDecarbFactor = Math.pow(0.985, year - 2023);

    // Buildings policy ramps (linear from 2023 -> 2045)
    const ramp = clampNumber(yearsElapsed / totalSteps, 0, 1);
    const energyEfficiencyThisYear = targetEnergyEfficiencyPercent * ramp;
    const buildingElectrificationThisYear = targetBuildingElectrificationPercent * ramp;
    const organicsDiversionThisYear = targetOrganicsDiversionPercent * ramp;
    // Buildings:
    // - Project electricity as kWh * (g/kWh)
    // - Energy efficiency reduces kWh and therms linearly to 2045
    // - Building electrification replaces therms with electricity kWh:
    //     1 therm = 29.3 kWh thermal, heat pump COP = 3.5
    //     => added_kWh = therms_replaced * (29.3/3.5)
    const COP = 3.5;
    const KWH_PER_THERM = 29.3;

    const baseTherms = calculateTotalNaturalGasConsumption(baseline.buildingsEnergyData, year);
    const baseKwh = calculateTotalElectricityConsumption(baseline.buildingsEnergyData, year);

    const effFrac = clampNumber(energyEfficiencyThisYear / 100, 0, 1);
    const elecFrac = clampNumber(buildingElectrificationThisYear / 100, 0, 1);

    // Apply efficiency first (reduces demand), then electrify a share of remaining therms.
    const thermsAfterEff = baseTherms * (1 - effFrac);
    const thermsReplaced = thermsAfterEff * elecFrac;
    const thermsRemaining = Math.max(0, thermsAfterEff - thermsReplaced);

    const kwhAfterEff = baseKwh * (1 - effFrac);
    const addedKwh = thermsReplaced * (KWH_PER_THERM / COP);
    const totalKwh = kwhAfterEff + addedKwh;

    // Grid decarbonization modeled as the year electricity reaches zero emissions.
    // Linearly reduce from the 2023 Buildings-page value to 0 by `cleanElectricityYear`.
    const denom = Math.max(1, cleanElectricityYear - 2023);
    const t = clampNumber((year - 2023) / denom, 0, 1);
    const gPerKwhPolicy = baseG2023 * (1 - t);

    // If we're only changing the grid clean year (no efficiency, no building electrification),
    // keep natural gas exactly at baseline to avoid small numeric drift showing as "NG reductions".
    const naturalGasEmissionsPolicy =
      effFrac === 0 && elecFrac === 0
        ? (baseRow.naturalGasEmissions || 0)
        : (thermsRemaining * 5.29) / 1000; // metric tons
    const electricityEmissionsPolicy = (totalKwh * gPerKwhPolicy) / 1_000_000; // g -> metric tons
    
      // Apply organics diversion reduction to waste emissions
      const organicsDiversionFrac = clampNumber(organicsDiversionThisYear / 100, 0, 1);
      const wasteEmissionsPolicy = (baseRow.wasteEmissions || 0) * (1 - organicsDiversionFrac);
      
      return {
      year,
      lightDutyEmissions: Math.round(lightDutyEmissions),
        heavyDutyEmissions: Math.round((baseRow.heavyDutyEmissions || 0) * hdvScale),
        naturalGasEmissions: Math.round(naturalGasEmissionsPolicy),
        electricityEmissions: Math.round(electricityEmissionsPolicy),
        wasteEmissions: Math.round(wasteEmissionsPolicy),
        offroadEmissions: Math.round((baseRow.offroadEmissions || 0)),
      totalEmissions: Math.round(lightDutyEmissions +
          (baseRow.heavyDutyEmissions || 0) * hdvScale +
          naturalGasEmissionsPolicy +
          electricityEmissionsPolicy +
          wasteEmissionsPolicy +
          (baseRow.offroadEmissions || 0))
      } as TimeSeriesEmissionData;
    });
  
  return transformedProjections;
}

/**
 * Main function to generate time series data for policy impact tool
 * Uses baseline cache for instant policy transformations
 */
export async function generatePolicyTimeSeriesData(
  location: string,
  scenario: PolicyScenario
): Promise<PolicyTimeSeriesResult> {
  try {
    console.log('Generating policy time series data for', location, 'with scenario:', scenario);
    
    // First, try to get cached baseline (synchronous check - instant)
    let baseline = getCachedBaseline(location);
    
    if (!baseline) {
      // No cached baseline - calculate it (expensive, one-time)
      // Use lock mechanism to prevent concurrent calculations
      console.log('📊 No cached baseline found, calculating...');
      baseline = await getOrCreateCalculationPromise(location, () => calculateAndCacheBaseline(location));
    } else {
      // Use cached baseline immediately (instant)
      // Note: We skip validation here to avoid blocking. Validation happens
      // when data actually changes (via clearBaselineCache hooks in vmtDataService/buildingsEnergyDataService)
      console.log('⚡ Using cached baseline projections (instant)');
    }
    
    // Apply policy transformations to cached baseline (instant, synchronous)
    console.log('⚡ Applying policy transformations (instant)...');
    const transformedProjections = applyPolicyTransformations(baseline, scenario);
    
    // Combine historical (2005-2022) with 2023 (baseline year from historical) and projections (2024-2045)
    // 2023 is the baseline year - it comes from historical data, not projections
    // Baseline projections start at 2024 (no policies)
    // Transformed projections start at 2024 (with policies applied)
    const historicalUpTo2022 = baseline.historicalData.filter(d => d.year < 2023);
    const historical2023 = baseline.historicalData.find(d => d.year === 2023);
    
    // Baseline data: historical (2005-2022) + 2023 (baseline) + baseline projections (2024-2045)
    const baselineData = historical2023 
      ? [...historicalUpTo2022, historical2023, ...baseline.baselineProjections]
      : [...historicalUpTo2022, ...baseline.baselineProjections];
    
    // Projected with policies: historical (2005-2022) + transformed projections (2023-2045)
    // transformedProjections already includes 2023 (unchanged baseline) + 2024-2045 (with policies)
    const projectedWithPolicies = [...historicalUpTo2022, ...transformedProjections];
    
    return {
      baselineData,
      projectedData: projectedWithPolicies,
      location
    };
  } catch (error) {
    console.error('Error generating policy time series data:', error);
    throw error;
  }
}
